import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/apiService';
import EvaluationPrestataire, { EvalCible } from './EvaluationPrestataire';
import { AvisData } from './avisTypes';
import './AvisPrestataire.css';

interface CommandeEvaluationTabProps {
  // Type + id de la commande validee. L'avis est cree EN AMONT (hook a la validation +
  // backfill), jamais ici : cet onglet le LIT seulement.
  typeCommande: 'BC' | 'MS';
  commandeId: number;
  // L'onglet ne se deverrouille qu'une fois la commande validee (signature).
  validated: boolean;
  // Sens de la notation. Defaut 'prestataire' (acheteur -> prestataire, public). 'acheteur' = miroir
  // PRIVE prestataire -> acheteur (visible des 2 parties seules).
  cible?: EvalCible;
}

/**
 * Onglet "Évaluation" d'une commande (BC/MS). Lecture de l'avis (cree a la validation par le hook
 * serveur, jamais a l'ouverture) + edition par l'auteur ; le serveur tranche qui peut editer
 * (can_edit). `cible` choisit le sens : prestataire note (public) ou acheteur note (miroir prive).
 */
const CommandeEvaluationTab: React.FC<CommandeEvaluationTabProps> = ({
  typeCommande,
  commandeId,
  validated,
  cible = 'prestataire',
}) => {
  const [avis, setAvis] = useState<AvisData | null>(null);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const isAcheteur = cible === 'acheteur';

  useEffect(() => {
    if (!validated || !commandeId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const fetch = isAcheteur
      ? apiService.getAvisAcheteurCommande(typeCommande, commandeId)
      : apiService.getAvisPrestataireCommande(typeCommande, commandeId);
    fetch
      .then((res: { avis?: AvisData | null; can_edit?: boolean } | null) => {
        if (cancelled || !res) return;
        setAvis(res.avis ?? null);
        setCanEdit(Boolean(res.can_edit));
      })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [validated, typeCommande, commandeId, isAcheteur]);

  const handleSave = (note: number, commentaire: string) => {
    if (!avis || !canEdit) return;
    setSaving(true);
    const save = isAcheteur
      ? apiService.updateAvisAcheteur(avis.id, note, commentaire || null)
      : apiService.updateAvisPrestataire(avis.id, note, commentaire || null);
    save
      .then((res: { avis?: AvisData } | null) => {
        if (res?.avis) setAvis(res.avis);
      })
      .catch(() => undefined)
      .finally(() => setSaving(false));
  };

  if (!validated) {
    // Avant validation : seule la carte prestataire affiche le message (la carte acheteur reste muette
    // pour ne pas dupliquer "sera disponible une fois la commande validée").
    if (isAcheteur) return null;
    return (
      <div className="avis-eval">
        <p className="avis-eval-empty">
          L'évaluation sera disponible une fois la commande validée.
        </p>
      </div>
    );
  }

  return (
    <EvaluationPrestataire
      avis={avis}
      canEdit={canEdit}
      loading={loading}
      saving={saving}
      onSave={handleSave}
      cible={cible}
    />
  );
};

export default CommandeEvaluationTab;
